import { createSignal, type Accessor, type Setter } from 'solid-js';
import { getLogger } from '@trimble/logging';
import type {
  ConfigProviderOptions,
  FeatureFlagOverrides,
  FeatureFlagSource,
} from '../types';
import {
  FeatureFlag,
  getSourceFlagName,
  getSourceFlagNames,
} from '../featureFlags';

type Overrides = Readonly<Record<string, boolean>>;

const CONFIG_FLAGS: readonly FeatureFlag[] = Object.values(FeatureFlag).filter(
  (flag) => getSourceFlagName(flag, 'config') !== undefined
);

const CONFIG_NAMES: readonly string[] = getSourceFlagNames('config');

const readStoredOverrides = (storageKey: string): unknown => {
  const raw = localStorage.getItem(storageKey);
  if (raw === null) {
    return undefined;
  }
  try {
    return JSON.parse(raw);
  } catch (error: unknown) {
    getLogger().warn(
      `Ignoring invalid feature flag overrides in "${storageKey}":`,
      error
    );
    return undefined;
  }
};

/**
 * Feature flag source backed by static defaults (for example a per-environment
 * config file) and user overrides persisted in localStorage.
 *
 * Answers synchronously, also before `initialize` resolves. On load it drops
 * stored overrides of unknown flags and overrides equal to their default, so a
 * stale entry cannot pin a user to an old value after the default changes.
 */
export class ConfigFeatureFlagProvider
  implements FeatureFlagSource, FeatureFlagOverrides
{
  public readonly name = 'config';
  public readonly flags = CONFIG_FLAGS;
  private readonly overrides: Accessor<Overrides>;
  private readonly setOverrides: Setter<Overrides>;

  constructor(private readonly options: ConfigProviderOptions) {
    const [overrides, setOverrides] = createSignal<Overrides>(
      this.pruneOverrides(readStoredOverrides(options.storageKey))
    );
    this.overrides = overrides;
    this.setOverrides = setOverrides;
    this.persist();
  }

  public initialize(): Promise<void> {
    return Promise.resolve();
  }

  public hasFlag(flag: FeatureFlag): boolean {
    const name = getSourceFlagName(flag, 'config');
    if (name === undefined) {
      return false;
    }
    return this.overrides()[name] ?? this.isDefaultEnabled(name);
  }

  public setOverride(flag: FeatureFlag, enabled: boolean): void {
    const name = getSourceFlagName(flag, 'config');
    if (name === undefined) {
      return;
    }
    const next = { ...this.overrides() };
    if (enabled === this.isDefaultEnabled(name)) {
      delete next[name];
    } else {
      next[name] = enabled;
    }
    this.setOverrides(next);
    this.persist();
  }

  private isDefaultEnabled(name: string): boolean {
    return this.options.defaults[name] ?? false;
  }

  private pruneOverrides(stored: unknown): Overrides {
    const pruned: Record<string, boolean> = {};
    if (typeof stored !== 'object' || stored === null) {
      return pruned;
    }
    for (const name of CONFIG_NAMES) {
      const value: unknown = Reflect.get(stored, name);
      if (typeof value === 'boolean' && value !== this.isDefaultEnabled(name)) {
        pruned[name] = value;
      }
    }
    return pruned;
  }

  private persist(): void {
    localStorage.setItem(
      this.options.storageKey,
      JSON.stringify(this.overrides())
    );
  }
}
